import { useState, useRef, useCallback } from 'react';
import { useFormContext, FieldPath, useWatch } from 'react-hook-form';
import { OrderSchemaType, ItemOrderSchemaType, ItemPath } from '../order.schema';

type AttachmentFieldPath = Extract<FieldPath<OrderSchemaType>, 'attachments'> | `${ItemPath}.${string}.anexos`;

interface AttachmentHandlerProps {
  fieldPath: AttachmentFieldPath;
}

const MAX_TOTAL_SIZE = 5 * 1024 * 1024;

export const useAttachmentHandler = ({ fieldPath }: AttachmentHandlerProps) => {
  const { control, setValue, getValues } = useFormContext<OrderSchemaType>();
  const attachmentNames: string[] = useWatch({ control, name: fieldPath, defaultValue: [] }) || [];

  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelectClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveAttachment = useCallback((indexToRemove: number) => {
    const currentFileNames: string[] = getValues(fieldPath) || [];
    const newFileNames = currentFileNames.filter((_, index) => index !== indexToRemove);
    
    const newSelectedFiles = selectedFiles.filter((_, index) => index !== indexToRemove);
    setSelectedFiles(newSelectedFiles);
    
    setValue(fieldPath, newFileNames, { shouldValidate: true, shouldDirty: true });

    const totalSize = newSelectedFiles.reduce((acc, file) => acc + file.size, 0);
    if (totalSize <= MAX_TOTAL_SIZE) {
      setAttachmentError(null);
    }
  }, [getValues, setValue, fieldPath, selectedFiles]);

  const handleRemoveAllAttachments = useCallback(() => {
    setValue(fieldPath, [], { shouldValidate: true });
    setSelectedFiles([]);
    setAttachmentError(null);
  }, [fieldPath, setValue]);


  
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentFileNames: string[] = getValues(fieldPath) || [];
    const newFileNames = [...currentFileNames, ...files.map(file => file.name)];
    const newSelectedFiles = [...selectedFiles, ...files];

    const totalSize = newSelectedFiles.reduce((acc, file) => acc + file.size, 0);

    if (totalSize > MAX_TOTAL_SIZE) {
      setAttachmentError('O tamanho total dos anexos não pode exceder 5MB.');
      return;
    }

    setValue(fieldPath, newFileNames, { shouldValidate: true });
    setSelectedFiles(newSelectedFiles);
    setAttachmentError(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [getValues, fieldPath, setValue, selectedFiles]);

  return {
    attachments: selectedFiles, 
    attachmentNames,
    attachmentError,
    fileInputRef,
    handleFileSelectClick,
    handleFileChange,
    handleRemoveAttachment,
    handleRemoveAllAttachments,
  };
};